import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { formatPrice } from '../utils/formatPrice';
import { API_BASE_URL, getAuthHeaders } from '../services/api';
import DealSlipViewModal from '../components/DealSlipViewModal';
import DocumentUpload from '../components/DocumentUpload';
import DocumentList from '../components/DocumentList';
import LimitExceededNotice from '../components/LimitExceededNotice';

export default function FrontOfficeBuybackBlotter() {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [limitExceededInfo, setLimitExceededInfo] = useState(null);
  const [approvingId, setApprovingId] = useState(null);
  const [viewingTransaction, setViewingTransaction] = useState(null);
  const [documentTransaction, setDocumentTransaction] = useState(null);

  useEffect(() => {
    fetchTransactions();
  }, []);

  const fetchTransactions = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${API_BASE_URL}/buyback/status/Pending_Verification`, { headers: getAuthHeaders() });
      if (response.data?.success) {
        setTransactions(response.data.data || []);
      } else {
        setTransactions([]);
      }
    } catch (err) {
      setError('Failed to fetch transactions');
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id) => {
    setApprovingId(id);
    setError('');
    setLimitExceededInfo(null);
    try {
      await axios.patch(`${API_BASE_URL}/buyback/${id}/status`, {
        status: 'Verified',
        action: 'verify'
      }, { headers: getAuthHeaders() });
      // Remove the approved transaction from the list
      setTransactions(transactions.filter(tx => tx.id !== id));
    } catch (err) {
      if (err.response?.data?.limitExceeded) {
        setLimitExceededInfo(err.response.data);
      } else {
        setError('Failed to approve transaction');
      }
    } finally {
      setApprovingId(null);
    }
  };

  const handleReject = async (id) => {
    setApprovingId(id);
    setError('');
    try {
      await axios.patch(`${API_BASE_URL}/buyback/${id}/status`, {
        status: 'Rejected',
        action: 'reject'
      }, { headers: getAuthHeaders() });
      // Remove the rejected transaction from the list
      setTransactions(transactions.filter(tx => tx.id !== id));
    } catch (err) {
      setError('Failed to reject transaction');
    } finally {
      setApprovingId(null);
    }
  };

  return (
    <div className="sister-blotter">
      <h2 style={{ fontSize: '2rem', marginBottom: '16px' }}>Front Office Buyback Blotter</h2>
      <p style={{ color: '#666', marginBottom: '24px' }}>Review and approve buyback deals for verification</p>
      
      {loading && <div>Loading...</div>}
      {error && <div style={{ color: 'red', marginBottom: 16 }}>{error}</div>}
      <LimitExceededNotice info={limitExceededInfo} onDismiss={() => setLimitExceededInfo(null)} />
      {!loading && transactions.length === 0 && <div>No buyback deals pending front office approval.</div>}
      
      {!loading && transactions.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 16 }}>
          <thead style={{ background: '#f1f3f7' }}>
            <tr>
              <th style={{ padding: '8px', border: '1px solid #eee' }}>Deal Number</th>
              <th style={{ padding: '8px', border: '1px solid #eee' }}>Trade Date</th>
              <th style={{ padding: '8px', border: '1px solid #eee' }}>Value Date</th>
              <th style={{ padding: '8px', border: '1px solid #eee' }}>ISIN</th>
              <th style={{ padding: '8px', border: '1px solid #eee' }}>Face Value</th>
              <th style={{ padding: '8px', border: '1px solid #eee' }}>Leg 1 Settlement</th>
              <th style={{ padding: '8px', border: '1px solid #eee' }}>Leg 2 Settlement</th>
              <th style={{ padding: '8px', border: '1px solid #eee' }}>Interest Rate</th>
              <th style={{ padding: '8px', border: '1px solid #eee' }}>Status</th>
              <th style={{ padding: '8px', border: '1px solid #eee' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {transactions.map(tx => (
              <tr key={tx.id}>
                <td style={{ padding: '8px', border: '1px solid #eee' }}>
                  <strong style={{ color: '#007bff' }}>{tx.deal_number}</strong>
                </td>
                <td style={{ padding: '8px', border: '1px solid #eee' }}>
                  {tx.leg1_trade_date ? format(new Date(tx.leg1_trade_date), 'yyyy-MM-dd') : ''}
                </td>
                <td style={{ padding: '8px', border: '1px solid #eee' }}>
                  {tx.leg1_value_date ? format(new Date(tx.leg1_value_date), 'yyyy-MM-dd') : ''}
                </td>
                <td style={{ padding: '8px', border: '1px solid #eee' }}>
                  <span style={{ fontFamily: 'monospace', fontSize: '0.85em' }}>
                    {tx.leg1_isin}
                  </span>
                </td>
                <td style={{ padding: '8px', border: '1px solid #eee' }}>
                  {formatPrice(tx.leg1_face_value)}
                </td>
                <td style={{ padding: '8px', border: '1px solid #eee' }}>
                  {formatPrice(tx.leg1_settlement_amount)}
                </td>
                <td style={{ padding: '8px', border: '1px solid #eee' }}>
                  {formatPrice(tx.leg2_settlement_amount)}
                </td>
                <td style={{ padding: '8px', border: '1px solid #eee' }}>
                  <span style={{ fontWeight: 'bold', color: '#28a745' }}>
                    {parseFloat(tx.leg1_interest_rate || 0).toFixed(2)}%
                  </span>
                </td>
                <td style={{ padding: '8px', border: '1px solid #eee' }}>
                  <span 
                    className={`badge ${
                      tx.deal_status === 'Approved' ? 'badge-success' :
                      tx.deal_status === 'Pending_Final_Approval' ? 'badge-warning' :
                      tx.deal_status === 'Verified' ? 'badge-info' :
                      tx.deal_status === 'Pending_Verification' ? 'badge-warning' :
                      tx.deal_status === 'Rejected' ? 'badge-danger' :
                      'badge-secondary'
                    }`}
                  >
                    {tx.deal_status?.replace(/_/g, ' ') || 'Pending'}
                  </span>
                </td>
                <td style={{ padding: '8px', border: '1px solid #eee' }}>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <button 
                      onClick={() => setViewingTransaction(tx)} 
                      style={{ 
                        padding: '6px 14px', 
                        background: '#007bff', 
                        color: '#fff', 
                        border: 'none', 
                        borderRadius: 4, 
                        cursor: 'pointer',
                        fontSize: '0.85em'
                      }}
                    >
                      View
                    </button>
                    <button 
                      onClick={() => setDocumentTransaction(tx)} 
                      style={{ 
                        padding: '6px 14px', 
                        background: '#6c757d', 
                        color: '#fff', 
                        border: 'none', 
                        borderRadius: 4, 
                        cursor: 'pointer',
                        fontSize: '0.85em'
                      }}
                      title="View/Upload Documents"
                    >
                      📎 Docs
                    </button>
                    <button 
                      onClick={() => handleApprove(tx.id)} 
                      disabled={approvingId === tx.id}
                      style={{ 
                        padding: '6px 14px', 
                        background: '#28a745', 
                        color: '#fff', 
                        border: 'none', 
                        borderRadius: 4, 
                        cursor: 'pointer',
                        fontSize: '0.85em'
                      }}
                    >
                      {approvingId === tx.id ? 'Approving...' : 'Approve'}
                    </button>
                    <button 
                      onClick={() => handleReject(tx.id)} 
                      disabled={approvingId === tx.id}
                      style={{ 
                        padding: '6px 14px', 
                        background: '#dc3545', 
                        color: '#fff', 
                        border: 'none', 
                        borderRadius: 4, 
                        cursor: 'pointer',
                        fontSize: '0.85em'
                      }}
                    >
                      {approvingId === tx.id ? 'Rejecting...' : 'Reject'}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      
      <div style={{ marginTop: '24px', textAlign: 'center' }}>
        <button 
          onClick={fetchTransactions}
          disabled={loading}
          style={{ 
            padding: '12px 24px', 
            background: '#007bff', 
            color: '#fff', 
            border: 'none', 
            borderRadius: 4, 
            cursor: 'pointer',
            fontSize: '1rem'
          }}
        >
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>
      
      {viewingTransaction && (
        <DealSlipViewModal
          transaction={viewingTransaction}
          onClose={() => setViewingTransaction(null)}
          dealType="Buyback"
        />
      )}
      
      {documentTransaction && (
        <div style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          background: 'rgba(0,0,0,0.5)', 
          zIndex: 1000, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          padding: '20px'
        }}>
          <div style={{ 
            background: '#fff', 
            borderRadius: '8px', 
            padding: '24px', 
            maxWidth: '800px', 
            width: '100%', 
            maxHeight: '90vh', 
            overflow: 'auto' 
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0 }}>
                Documents - Deal #: {documentTransaction.id || documentTransaction.deal_number}
              </h3>
              <button 
                onClick={() => setDocumentTransaction(null)}
                style={{ 
                  padding: '8px 16px', 
                  background: '#6c757d', 
                  color: '#fff', 
                  border: 'none', 
                  borderRadius: 4, 
                  cursor: 'pointer' 
                }}
              >
                Close
              </button>
            </div>
            <DocumentUpload
              transactionType="buyback"
              transactionId={documentTransaction.id || documentTransaction.deal_number}
              onUploadSuccess={() => {}}
              onUploadError={(error) => console.error('Upload error:', error)}
              multiple={true}
            />
            <DocumentList
              transactionType="buyback"
              transactionId={documentTransaction.id || documentTransaction.deal_number}
              onDocumentDeleted={() => {}}
            />
          </div>
        </div>
      )}
    </div>
  );
}
